
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MasterLicenseDashboard from '@/pages/MasterLicenseDashboard';
import MasterLicenseKeys from '@/pages/MasterLicenseKeys';
import MasterLicenseDomains from '@/pages/MasterLicenseDomains';
import MasterLicenseSuperAdmins from '@/pages/MasterLicenseSuperAdmins';
import MasterLicenseResellerPlans from '@/pages/MasterLicenseResellerPlans';
import MasterLicenseResellerSales from '@/pages/MasterLicenseResellerSales';
import MasterLicenseResellerCRM from '@/pages/MasterLicenseResellerCRM';
import MasterLicenseReferralBank from '@/pages/MasterLicenseReferralBank';
import MasterLicenseSettings from '@/pages/MasterLicenseSettings';
import MasterLicenseSupportManagement from '@/pages/MasterLicenseSupportManagement';
import MasterLicenseKnowledgeBase from '@/pages/MasterLicenseKnowledgeBase';
import MasterLicenseProfile from '@/pages/MasterLicenseProfile';
import MasterLicensePendingOrders from '@/pages/MasterLicensePendingOrders';
import MasterLicenseUserManagement from '@/pages/MasterLicenseUserManagement';
import MasterLicenseRewardSales from '@/pages/MasterLicenseRewardSales';
import MasterLicenseBlogManagement from '@/pages/MasterLicenseBlogManagement';
import ResellerOrderDetail from '@/pages/ResellerOrderDetail';
import MasterAIBannerPage from '@/pages/master/MasterAIBannerPage';
import MasterBannerCreatorPage from '@/pages/master/MasterBannerCreatorPage';
import MasterManageBannersPage from '@/pages/master/MasterManageBannersPage';
import BannerCreationPage from '@/pages/master/BannerCreationPage';
import BannerTemplatesPage from '@/pages/master/BannerTemplatesPage';
import { useAuth } from '@/context/SupabaseAuthContext';
import { Toaster } from '@/components/ui/toaster';
import { MasterLicenseSettingsProvider } from '@/contexts/MasterLicenseSettingsContext';

interface MasterLicenseRoutesProps {
  isLoggedIn?: boolean;
  userRole?: string | null;
}

const MasterLicenseRoutes: React.FC<MasterLicenseRoutesProps> = ({ isLoggedIn, userRole }) => {
  const authContext = useAuth();
  const isAuthenticated = isLoggedIn !== undefined ? isLoggedIn : !!authContext.user;
  const role = userRole || authContext.role;
  const user = authContext.user;
  
  // Check if user is a master admin by role or email
  const isMasterAdmin = role === 'masterAdmin' || 
                        user?.email?.includes('master') || 
                        localStorage.getItem('isMasterAdmin') === 'true';
  
  console.log('MasterLicenseRoutes rendering with:', { 
    isAuthenticated, 
    role, 
    isMasterAdmin,
    userEmail: user?.email,
    pathname: window.location.pathname
  });
  
  if (authContext.loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  // Modified logic to be more permissive for master admin access
  if (!isAuthenticated && process.env.NODE_ENV !== 'development') {
    console.log('MasterLicenseRoutes: User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('Rendering MasterLicenseRoutes component with available routes');
  
  return (
    <MasterLicenseSettingsProvider isLoading={false}>
      <Toaster />
      <Routes>
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<MasterLicenseDashboard />} />
        <Route path="license-keys" element={<MasterLicenseKeys />} />
        <Route path="domains" element={<MasterLicenseDomains />} />
        <Route path="super-admins" element={<MasterLicenseSuperAdmins />} />
        <Route path="users" element={<MasterLicenseUserManagement />} />
        <Route path="blog" element={<MasterLicenseBlogManagement />} />
        <Route path="pending-orders" element={<MasterLicensePendingOrders />} />
        <Route path="settings" element={<MasterLicenseSettings />} />
        <Route path="settings/orders/:id" element={<ResellerOrderDetail />} />
        <Route path="settings/orders/:id/edit" element={<ResellerOrderDetail isEditMode={true} />} />
        <Route path="support" element={<MasterLicenseSupportManagement />} />
        <Route path="knowledge-base" element={<MasterLicenseKnowledgeBase />} />
        <Route path="profile" element={<MasterLicenseProfile />} />
        <Route path="reseller-plans" element={<MasterLicenseResellerPlans />} />
        <Route path="reseller-sales" element={<MasterLicenseResellerSales />} />
        <Route path="reseller-crm" element={<MasterLicenseResellerCRM />} />
        <Route path="referral-bank" element={<MasterLicenseReferralBank />} />
        <Route path="reward-sales" element={<MasterLicenseRewardSales />} />
        
        {/* Marketing Routes */}
        <Route path="marketing/banner-templates" element={<BannerTemplatesPage />} />
        <Route path="marketing/ai-banner" element={<MasterAIBannerPage />} />
        <Route path="marketing/banner-creator" element={<MasterBannerCreatorPage />} />
        <Route path="marketing/manage-banners" element={<MasterManageBannersPage />} />
        <Route path="marketing/banner-creation" element={<BannerCreationPage />} />
        
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </MasterLicenseSettingsProvider>
  );
};

export default MasterLicenseRoutes;
